import React from "react";
import {
  Lock,
  CheckCircle2,
  AlertCircle,
  FileText,
  ShieldAlert,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "src/components/ui/Button";

interface EnrollmentLedgerSidebarProps {
  totalPurchasePrice: number;
  actualPurchasePrice: number;
  amountReceived: number;
  amountDue: number;
  currency: string;
  totalSessions: number;
  actualRate: number;
  catalogRate: number;
  productType?: "booster" | "custom" | "preparation";
  targetCount: number;
  scopeLabel?: string;
  isSubmitting: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
}

export const EnrollmentLedgerSidebar: React.FC<EnrollmentLedgerSidebarProps> = ({
  totalPurchasePrice,
  actualPurchasePrice,
  amountReceived,
  amountDue,
  currency,
  totalSessions,
  actualRate,
  catalogRate,
  productType,
  targetCount,
  scopeLabel,
  isSubmitting,
  canSubmit,
  onSubmit,
}) => {
  const isOverridden = catalogRate > 0 && Math.abs(actualRate - catalogRate) > 0.001;
  const discountAmount = totalPurchasePrice - actualPurchasePrice;
  const discountPercentage = totalPurchasePrice > 0 ? (discountAmount / totalPurchasePrice) * 100 : 0;

  return (
    <div className="space-y-4 lg:sticky lg:top-6">
      {/* Primary Financial Ledger Card */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-md overflow-hidden">
        {/* Executive Header */}
        <div className="bg-slate-900 text-white p-5">
          <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
            <span className="uppercase tracking-wider font-semibold text-[11px]">
              Settlement Ledger
            </span>
            <span className="font-mono text-purple-300 font-bold uppercase">
              {currency || "USD"}
            </span>
          </div>

          <div className="flex items-baseline justify-between mt-2">
            <span className="text-slate-400 text-xs">Total Purchase:</span>
            <span className="text-2xl sm:text-3xl font-extrabold font-mono tracking-tight text-white">
              ${Number.isFinite(actualPurchasePrice) ? actualPurchasePrice.toFixed(2) : "0.00"}
            </span>
          </div>

          <div className="flex items-center justify-between mt-1 text-[11px] text-purple-200">
            <span>Effective Rate:</span>
            <span className="font-mono font-medium">
              {totalSessions > 0 ? `${totalSessions} sess × $${actualRate.toFixed(2)}/sess` : "Rate not set"}
            </span>
          </div>
        </div>

        {/* Commercial Breakdown */}
        <div className="p-5 space-y-3.5 text-xs">
          {isOverridden && (
            <>
              <div className="flex items-center justify-between text-slate-500">
                <span>Standard Catalog Price:</span>
                <span className="font-mono font-medium line-through">
                  ${totalPurchasePrice.toFixed(2)}
                </span>
              </div>

              <div
                className={`flex items-center justify-between p-2 rounded-lg border text-xs ${
                  discountAmount > 0
                    ? "text-emerald-700 bg-emerald-50/70 border-emerald-100"
                    : "text-amber-800 bg-amber-50/70 border-amber-200"
                }`}
              >
                <span className="font-semibold">
                  {discountAmount > 0 ? "Applied Discount:" : "Rate Premium:"}
                </span>
                <span className="font-mono font-bold">
                  {discountAmount > 0 ? "-" : "+"}${Math.abs(discountAmount).toFixed(2)} (
                  {Math.abs(discountPercentage).toFixed(1)}%)
                </span>
              </div>
            </>
          )}

          <div className="flex items-center justify-between font-semibold text-slate-900 pt-1">
            <span>Settlement Received Today:</span>
            <span className="font-mono font-bold text-slate-900 text-sm">
              ${Number.isFinite(amountReceived) ? amountReceived.toFixed(2) : "0.00"}
            </span>
          </div>

          <div className="flex items-center justify-between text-slate-500">
            <span>Outstanding Balance:</span>
            <span
              className={`font-mono font-medium ${
                amountDue > 0 ? "text-amber-600 font-bold" : "text-slate-600"
              }`}
            >
              ${Number.isFinite(amountDue) ? amountDue.toFixed(2) : "0.00"}
            </span>
          </div>

          {/* Canonical Plan Integrity Checklist */}
          <div className="pt-3 border-t border-slate-100">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-2">
              Canonical Plan Integrity
            </span>
            <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-slate-200/80 text-[11px] text-slate-600">
              <div className="flex items-center justify-between">
                <span>Target Plan:</span>
                <span className="font-mono font-bold text-slate-900">
                  {productType === "booster"
                    ? targetCount > 0
                      ? `${targetCount} frozen objectives`
                      : "Full Published Scope"
                    : productType === "custom"
                    ? `${targetCount} target selection${targetCount === 1 ? "" : "s"}`
                    : productType === "preparation"
                    ? "Approved Blueprint"
                    : "Unselected"}
                </span>
              </div>

              {scopeLabel && (
                <div className="flex items-center justify-between">
                  <span>Scope Version:</span>
                  <span className="font-mono text-slate-800 truncate max-w-[170px]" title={scopeLabel}>
                    {scopeLabel}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span>Prerequisites:</span>
                <span className="text-emerald-700 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Auto-Bundled
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span>Batch Creation:</span>
                <span className="text-purple-700 font-medium">Eligible upon freeze</span>
              </div>
            </div>
          </div>

          {/* Atomic Transaction Callout */}
          <div className="p-3 bg-purple-50/60 border border-purple-200/80 rounded-xl text-[11px] text-purple-900 space-y-1">
            <div className="flex items-center gap-1.5 font-bold">
              <Lock className="w-3.5 h-3.5 text-purple-700 shrink-0" />
              <span>Immutable Plan Freeze</span>
            </div>
            <p className="text-purple-800 leading-relaxed">
              Finalization records an immutable objective snapshot in{" "}
              <code className="font-mono text-[10px] bg-purple-100 px-1 py-0.5 rounded">
                subscription_curriculum
              </code>{" "}
              and activates access in a single atomic commit.
            </p>
          </div>

          {/* Action CTA */}
          <div className="pt-2">
            <Button
              type="button"
              disabled={isSubmitting || !canSubmit}
              onClick={onSubmit}
              className="w-full py-3 px-4 rounded-xl font-bold text-xs bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-300 transition-all duration-150 flex items-center justify-center gap-2 disabled:bg-slate-300 disabled:shadow-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Freezing Plan & Activating...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Verify Payment & Freeze Plan</span>
                </>
              )}
            </Button>
            <p className="text-center text-[10px] text-slate-400 mt-2">
              Transaction audited by role: <span className="font-mono">admin_dispatcher</span>
            </p>
          </div>
        </div>
      </div>

      {/* Auxiliary Security Note */}
      <div className="bg-white p-3.5 rounded-xl border border-slate-200 text-xs text-slate-500 space-y-1">
        <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700">
          <span>Database Integrity Check</span>
          <span className="text-emerald-600 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Verified
          </span>
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Objectives are resolved exclusively via protected database functions to prevent untrusted hierarchy injection.
        </p>
      </div>
    </div>
  );
};

